import React, {FC} from 'react';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {InterfaceEventsEnum} from '../../../../../services/tracking';
import {
  getAnalyticsFilters,
  getHasDiffFilters,
} from '../../../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {
  getAnalyticsSelectedNamespace,
  getAnalyticsSelectedViewId,
} from '../../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {updateSavedViewAsyncAction} from '../../../../../store/panels/analytics/views/analyticsPanelViewsActions';
import {makeGetSelectedView} from '../../../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {
  AnalyticsSelectedViewId,
  isAnalyticsViewSaved,
} from '../../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {mapFiltersToTerms} from '../../../../helpers/analyticsSavedViewsHelpers';
import {useAnalyticsTracking} from '../../../../helpers/tracking/useAnalyticsTracking';
import {ReportHeaderSaveViewDialog} from './reportHeaderSaveViewDialog';

/*
 * Props.
 */

interface ReportHeaderSaveViewDropdownProps {}

/*
 * Component.
 */

export const ReportHeaderSaveViewDropdown: FC<ReportHeaderSaveViewDropdownProps> = () => {
  const [shouldShowSaveAsNewDialog, toggleSaveAsNewDialog] = useToggle();

  const dispatch = useAppDispatch();

  const onSaveViewDialogConfirm = toggleSaveAsNewDialog;
  const onSaveViewDialogClose = toggleSaveAsNewDialog;

  const selectedViewId = useAppSelector(getAnalyticsSelectedViewId);
  const filters = useAppSelector(getAnalyticsFilters);
  const selectedView = useAppSelector(makeGetSelectedView(selectedViewId));
  const selectedNamespace = useAppSelector(getAnalyticsSelectedNamespace);
  const trackAnalytics = useAnalyticsTracking();
  const {hasDiffFilters} = useAppSelector(getHasDiffFilters);

  // Don't show the "Save view" button if the filters are empty
  // or we are in a saved view that does not have edited filters.
  const shouldDisableDropdown = !hasDiffFilters;

  const updateSavedViewFilters = async () => {
    if (!selectedView) {
      throw new Error(`Unable to find view ${selectedViewId}`);
    }

    const terms = mapFiltersToTerms(filters);

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_SAVE_VIEW_CLICK);

    dispatch(
      updateSavedViewAsyncAction(
        {...selectedView, filters: {...selectedView.filters, terms}},
        selectedNamespace,
      ),
    );
  };

  // Hide the button if it would otherwise be disabled.
  if (shouldDisableDropdown) {
    return null;
  }

  return (
    <>
      <PopoverCoordinator
        renderButton={() => (
          <Button variant={ButtonVariantsEnum.PRIMARY} isDisabled={shouldDisableDropdown}>
            <Trans id="saved-view-dropdown-save-view">Save View</Trans>
          </Button>
        )}
        renderModal={(popoverProps) => (
          <SimpleDropdown {...popoverProps}>
            {maybeRenderSave(selectedViewId, updateSavedViewFilters)}
            {maybeRenderSaveAsNew(selectedViewId, toggleSaveAsNewDialog)}
          </SimpleDropdown>
        )}
        isDisabled={shouldDisableDropdown}
      />
      {maybeRenderSaveViewDialog(shouldShowSaveAsNewDialog, onSaveViewDialogConfirm, onSaveViewDialogClose)}
    </>
  );
};

/*
 * Helpers.
 */

const maybeRenderSaveViewDialog = (
  shouldShowSaveAsNewDialog: boolean,
  onSaveViewDialogConfirm: Handler,
  onSaveViewDialogClose: Handler,
) => {
  if (!shouldShowSaveAsNewDialog) {
    return null;
  }

  return (
    <ReportHeaderSaveViewDialog onOkClick={onSaveViewDialogConfirm} onRequestClose={onSaveViewDialogClose} />
  );
};

const maybeRenderSave = (selectedViewId: AnalyticsSelectedViewId, updateSavedViewFilters: Handler) => {
  if (!isAnalyticsViewSaved(selectedViewId)) {
    return null;
  }

  return (
    <SimpleDropdownItem checkmarkLabel="Save" onSelect={updateSavedViewFilters}>
      <Trans id="saved-view-dropdown-save">Save</Trans>
    </SimpleDropdownItem>
  );
};

const maybeRenderSaveAsNew = (selectedViewId: AnalyticsSelectedViewId, toggleSaveAsNewDialog: Handler) => {
  if (typeof selectedViewId === 'undefined') {
    return null;
  }

  return (
    <SimpleDropdownItem checkmarkLabel="Save as new" onSelect={toggleSaveAsNewDialog}>
      <Trans id="saved-view-dropdown-save-new">Save as new</Trans>
    </SimpleDropdownItem>
  );
};
